import type { ButtonSize } from '@timelyapp/react';
import { Button, useThrottledCallback } from '@timelyapp/react';
import Popover from 'components/popovers/Popover';
import { lighterColorPalette, naturalColorPalette } from 'constants/colorPalette';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { stripHexPrefix } from 'utils/colors';
import ColorPicker from './ColorPicker';
import styles from './ColorPickerDropdown.module.scss';
import HexColorPicker from './HexColorPicker';

const palettes = [naturalColorPalette, lighterColorPalette];

interface Props {
  color: string;
  size?: ButtonSize;
  disabled?: boolean;
  onChange: (color: string) => void;
  className?: string;
  preview?: (color: string) => React.ReactElement;
}

const ColorPickerDropdown = (props: Props) => {
  const [color, setColorInState] = useState(props.color);
  const onChangeThrottled = useThrottledCallback(props.onChange, 300);
  const onChange = useCallback(
    (c: string) => {
      const colorOnly = stripHexPrefix(c);
      setColorInState(colorOnly);
      onChangeThrottled(colorOnly);
    },
    [onChangeThrottled]
  );
  useEffect(() => {
    setColorInState(props.color);
  }, [props.color]);

  const [isOpen, setIsOpen] = useState(false);

  const handleToggleDropdown = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  function renderPreview() {
    const { preview } = props;
    if (!preview) {
      return;
    }

    return (
      <div className={styles.preview}>
        <div className={styles.previewTitle}>Preview</div>
        <div>{preview(stripHexPrefix(color))}</div>
      </div>
    );
  }

  const colorOptions = useMemo(() => {
    return palettes.flat();
  }, []);

  function renderDropdown() {
    return (
      <div className={styles.dropdownContainer}>
        {renderPreview()}
        {props.disabled ? (
          <div className={styles.colorGrid}>
            <div className={styles.colorGridItem} style={{ backgroundColor: `#${props.color}` }} />
          </div>
        ) : (
          <ColorPicker
            className={styles.colorGrid}
            color={color}
            defaultColors={colorOptions}
            setColor={onChange}
            size={props.size}
          />
        )}
        <HexColorPicker color={color} disabled={props.disabled} setColor={onChange} />
      </div>
    );
  }

  return (
    <Popover
      targetWrapperElement="span"
      closeOnClick={false}
      closeOnOutsideClick
      isOpen={isOpen}
      onOpen={handleToggleDropdown}
      onClose={handleClose}
      position="bottom"
      targetCaret={false}
      target={
        <Button className={styles.button} priority="medium" size={props.size}>
          <div
            className={styles.buttonColorPreview}
            style={{ backgroundColor: `#${props.color}` }}
          />
        </Button>
      }
    >
      {renderDropdown()}
    </Popover>
  );
};

export default ColorPickerDropdown;
